import styled from "styled-components";
import { AppRequirementsState } from "../../context/appRequirementsReducer";
import {
  useAppRequirementsContext,
  useAppRequirementsDispatchContext,
} from "../../context/ContextProvider";
import { Link } from "../../components/atoms/Link";
import { ExternalLinkIcon } from "../../components/atoms/ExternalLinkIcon";
import colours from "../../lib/colours";
import { TextArea } from "../../components/atoms/TextInput";
import {
  StyledRadioContainer,
  StyledRadioInput,
  StyledRadioLabel,
} from "../../components/molecules/RadioButtons";
import {
  applicantsGuide,
  guidelines,
  medianCreditPricing,
} from "../../lib/constants";

const Container = styled.div`
  padding-bottom: 24px;
`;

const SectionTitle = styled.h4`
  margin-top: 24px;
  margin-bottom: -8px;
  font-weight: 600;
  color: ${colours.th1};
`;

const ListItemTitle = styled.span`
  font-weight: 400;
  color: ${colours.th1};
`;

const StyledTextArea = styled(TextArea)`
  box-sizing: border-box;
  width: calc(100% - 24px);
  min-width: calc(100% - 24px);
  max-width: calc(100% - 24px);
  height: 100px;
  min-height: 60px;
  max-height: 460px;
  border: 2px solid ${colours.th2};
  outline: 1px solid ${colours.white};
  border-radius: 0px;

  &:focus-visible {
    border: 2px solid ${colours.th2d1};
    outline: 1px solid ${colours.th2d1};
    border-radius: 2px;
  }
`;

const StyledRadioGroupContainer = styled.div`
  margin-block: 12px;
  padding: 4px;
`;

const ItalicsSpan = styled.span`
  font-style: italic;
`;

const topoLandPlaceholder = `Example text - The land slopes gradually from east to west then has a steep slope towards a 
creek in the west of the property. There is a farm dam in the centre of the property.`;

const avoidMinimisePlaceholder = `Example text - There has been no strategic planning on my property.
I have sited my house to minimise impacts by:
•	Building in Location 1 and on native vegetation with the lowest condition score.
•	Building where no Large Trees need to be removed.
•	Making sure the house is more than 30 metres away from the creek. 
Nothing more can be done to avoid or minimise impacts because: 
•	Native vegetation covers most of the property. 
•	The house needs to be built close to the utility connection points. Areas without native vegetation are too far away from these points. 
`;

const defendableSpacePlaceholder = `Example text - The bushfire threat in the vicinity of our dwelling is a significant concern. The adjoining land is characterised by dry, combustible vegetation and there is a history of bushfire incidents in the area. Before proposing the removal of native vegetation for creating defendable space, we explored and considered a range of alternative bushfire risk mitigation measures, including:
•	Bushfire-Resistant Building Design: We have designed and constructed our dwelling in accordance with bushfire-resistant building standards. 
•	Fuel Load Reduction: We are committed to implementing a regular fuel load reduction program, which involves maintaining a clear zone around our property, regularly mowing grass, and removing dead vegetation and debris.
•	Water Supply: We will establish an on-site water supply, including a large water tank and a firefighting pump, to provide an immediate water source for fire suppression efforts.
•	Emergency Evacuation Plan: We have developed a comprehensive emergency evacuation plan for all residents and guests on the property. This plan includes designated assembly points, communication procedures, and regular drills to ensure everyone is prepared for a bushfire emergency.
Despite these efforts, it has been determined that it is still necessary to remove the native vegetation identified in this Native Vegetation Removal Report to create defendable space to reduce the risk of bushfire impacting our dwelling. 
`;

const nativeVegPrecinctPlanPlaceholder = `Example text - I am removing native vegetation that was identified as to be retained in the Happy Valley Native Vegetation Precinct Plan.

The NVPP objective is to maintain riparian vegetation and fauna habitat along the creeks running through the precinct. I am proposing to remove two Scattered Trees which are in an area of my property which is 50 metres from the Green Lake Creek. The trees are isolated and do not contribute to the habitat values of the riparian vegetation.

The removal of these two trees will not fragment any areas of vegetation identified for retention. Due to the poor health of the trees and the existing land use, it is very unlikely that the trees will be viable in the long-term. 
`;

const offsetStatementPlaceholder = `Example text – 
Third-party offset:
I am going to purchase the offset I need from an existing native vegetation credit site. I have attached an email from a Native Vegetation Credit Register accredited broker/DEECA to this application, which states that the offset is available. I have checked and understand the likely cost of the offset.
or
First-party offset:
I will meet the offset requirement by protecting native vegetation on my own property. I have attached the Native Vegetation Offset Report. Council has agreed to secure the site via a Section 173 Agreement – the confirmation letter is attached. I have checked and understand the costs and requirements to secure the offset site.
`;

function AppRequirements() {
  const state: AppRequirementsState = useAppRequirementsContext();
  const dispatch = useAppRequirementsDispatchContext();

  const onPropertyVegPlanRadioChange = (e: any) => {
    dispatch({
      type: "change",
      payload: { prop: "propertyVegPlan", value: e.target.value === "true" },
    });
  };

  const onNativeVegPrecinctPlanRadioChange = (e: any) => {
    dispatch({
      type: "change",
      payload: {
        prop: "nativeVegPrecinctPlan",
        value: e.target.value === "true",
      },
    });
  };

  return (
    <Container>
      <p>
        Clearing proposals in the Basic or Intermediate Assessment Pathway must
        address the nine application requirements outlined in Table 4 of the{" "}
        <Link
          href={guidelines}
          target="_blank"
          title="Guidelines for the removal, destruction or lopping of native vegetation"
          rel="noopener noreferrer"
        >
          Guidelines for the removal, destruction or lopping of native
          vegetation
        </Link>
        <ExternalLinkIcon /> (the Guidelines).
      </p>
      <p>
        The{" "}
        <Link
          href={applicantsGuide}
          target="_blank"
          title="Applicant’s Guide"
          rel="noopener noreferrer"
        >
          Applicant’s Guide
        </Link>
        <ExternalLinkIcon /> will assist applicants in navigating the approvals
        process and provides further guidance on addressing the following
        application requirements.
      </p>
      <p>You can choose to:</p>
      <ul>
        <li>
          Address the application requirements below, with your inputs included
          in the Native Vegetation Removal Report; or
        </li>
        <li>
          Address the below application requirements separately and include them
          as an attachment to your application. Please leave the fields blank
          and click "Next".
        </li>
      </ul>

      <SectionTitle>
        Application Requirement 1 - Native vegetation removal information
      </SectionTitle>
      <p>
        This requirement is addressed by using this tool to map the native
        vegetation proposed for removal.
      </p>

      <SectionTitle>
        Application Requirement 2 - Topographical and land information
      </SectionTitle>
      <p>
        The responsible authority uses this information to determine if the
        proposed removal of native vegetation will result in a decrease in
        ground and surface water quality and riparian ecosystems or an increase
        in land degradation.
      </p>
      <p>
        Describe the topographical and land features in the vicinity of the
        proposed works. Describe the location and extent of any ridges,
        hilltops, wetlands and waterways, and slopes of more than 20% gradient.
        Also note if there are any low-lying areas, saline discharge areas or
        areas of erosion.
      </p>
      <StyledTextArea
        maxLength={3000}
        placeholder={topoLandPlaceholder}
        value={state.topoLandInfo}
        rows={4}
        onChange={(e) =>
          dispatch({
            type: "change",
            payload: { prop: "topoLandInfo", value: e.target.value },
          })
        }
      ></StyledTextArea>

      <SectionTitle>
        Application Requirement 3 – Photos of the native vegetation to be
        removed
      </SectionTitle>
      <p>
        Application Requirement 3 is not addressed in this tool. All
        applications must include recent, timestamped photos of each Patch,
        Large Patch Tree and Scattered Tree which has been mapped in the Native
        Vegetation Removal Report. The photos must be an accurate representation
        of the vegetation. Photos of Scattered Trees should also include the
        understorey vegetation.
      </p>

      <SectionTitle>Application Requirement 4 – Past removal</SectionTitle>
      <p>
        Prior to mapping the native vegetation proposed for removal, you have
        been asked to account for any past removal. This information will be
        incorporated into the Native Vegetation Removal Report.
      </p>

      <SectionTitle>
        Application Requirement 5 - Avoid and minimise statement
      </SectionTitle>
      <p>
        The responsible authority uses this information to determine if the
        removal of native vegetation is to the minimum extent required to
        achieve the objectives of the project.
      </p>
      <p style={{ fontWeight: 500 }}>
        The avoidance and minimisation of native vegetation removal is extremely
        important for conserving Victoria’s biodiversity and is a key factor in
        reducing the costs associated with securing offsets.
      </p>
      <p>
        You must demonstrate that all efforts to avoid and minimise the extent
        of native vegetation proposed for removal have been considered and
        implemented if practical. The statement must address the three points
        below, as applicable.
      </p>
      <ul>
        <li>
          <ListItemTitle>Strategic level planning</ListItemTitle>
          <br />
          Indicate if the site has been included in a strategic level planning
          process which has identified areas of vegetation/biodiversity to avoid
          or minimise. This may have been a Planning Scheme Amendment, Native
          Vegetation Precinct Plan, Property Vegetation Plan, Regional Growth
          Plan or some other strategic environmental assessment which has been
          supported by DEECA or incorporated into the relevant planning scheme.
        </li>
        <li>
          <ListItemTitle>Site level planning</ListItemTitle>
          <br />
          Describe how you have designed the project footprint or construction
          method to avoid and minimise impacts on native vegetation within the
          property.
        </li>
        <li>
          <ListItemTitle>No further opportunities</ListItemTitle>
          <br />
          Acknowledge that there are no further opportunities to avoid native
          vegetation removal, without undermining the objectives of the project.
        </li>
      </ul>
      <StyledTextArea
        maxLength={3000}
        placeholder={avoidMinimisePlaceholder}
        value={state.avoidMinimiseStatement}
        rows={8}
        onChange={(e) =>
          dispatch({
            type: "change",
            payload: { prop: "avoidMinimiseStatement", value: e.target.value },
          })
        }
        style={{ height: "230px" }}
      ></StyledTextArea>

      <SectionTitle>
        Application Requirement 6 - Property Vegetation Plan
      </SectionTitle>
      <p>
        This requirement only applies if you have an approved Property
        Vegetation Plan (PVP) on the property. A PVP is an agreement made under
        Section 69 of the{" "}
        <ItalicsSpan>Conservation, Forests and Lands Act 1987</ItalicsSpan>{" "}
        between yourself and the Secretary to DEECA.
      </p>
      <p>
        A PVP is a plan which identifies native vegetation on the property to be
        removed or retained over a ten-year period and addresses all the
        application requirements needed for an application to remove native
        vegetation.
      </p>
      <p>Does a PVP apply to this proposal?</p>
      <StyledRadioGroupContainer
        style={{
          borderLeft:
            state.propertyVegPlan === null
              ? `4px solid ${colours.th2}`
              : "4px solid transparent",
        }}
      >
        <StyledRadioContainer>
          <StyledRadioInput
            type="radio"
            id="NoPropertyVegPlan"
            name="propertyVegPlan"
            value={"false"}
            onChange={onPropertyVegPlanRadioChange}
            checked={state.propertyVegPlan === false}
          />
          <StyledRadioLabel htmlFor={"NoPropertyVegPlan"}>No</StyledRadioLabel>
        </StyledRadioContainer>
        <StyledRadioContainer>
          <StyledRadioInput
            type="radio"
            id="YesPropertyVegPlan"
            name="propertyVegPlan"
            value={"true"}
            onChange={onPropertyVegPlanRadioChange}
            checked={state.propertyVegPlan === true}
          />
          <StyledRadioLabel htmlFor={"YesPropertyVegPlan"}>
            Yes, the PVP will be included in the application
          </StyledRadioLabel>
        </StyledRadioContainer>
      </StyledRadioGroupContainer>

      <SectionTitle>
        Application Requirement 7 - Defendable space statement
      </SectionTitle>
      <p>
        The responsible authority uses this information to assess the need to
        create defendable space, and whether alternative measures have been
        explored to avoid native vegetation removal.
      </p>
      <p>
        If the native vegetation removal is for creating defendable space to
        reduce bushfire risk, you must supply a statement that:
      </p>
      <ul>
        <li>Describes the bushfire threat; and</li>
        <li>
          Describes how other bushfire risk mitigation measures were considered
          to reduce the amount of native vegetation proposed for removal (this
          can also be part of the avoid and minimise statement).
        </li>
      </ul>
      <p>
        If the proposed defendable space is within the Bushfire Management
        Overlay (BMO), and in accordance with the{" "}
        <ItalicsSpan>
          ‘Exemption to create defendable space for a dwelling under Clause
          44.06 of local planning schemes’
        </ItalicsSpan>{" "}
        in Clause 52.12-5, this statement is not necessary because it forms part
        of the application requirements under Clause 44.06.
      </p>
      <p>
        To rely on the Clause 52.12-5 exemption, the adopted defendable space
        zone must not exceed the specifications outlined in Table 1 to Clause
        53.02-3.
      </p>
      <StyledTextArea
        maxLength={3000}
        placeholder={defendableSpacePlaceholder}
        value={state.defendableSpaceStatement}
        rows={17}
        onChange={(e) =>
          dispatch({
            type: "change",
            payload: {
              prop: "defendableSpaceStatement",
              value: e.target.value,
            },
          })
        }
        style={{ height: "380px" }}
      ></StyledTextArea>

      <SectionTitle>
        Application Requirement 8 - Native Vegetation Precinct Plan
      </SectionTitle>
      <p>
        This requirement is only applicable if you are removing native
        vegetation from within an area covered by a Native Vegetation Precinct
        Plan (NVPP), and the proposed removal is not identified as ‘to be
        removed’ within the NVPP.
      </p>
      <p>Does an NVPP apply to this proposal?</p>
      <StyledRadioGroupContainer
        style={{
          borderLeft:
            state.nativeVegPrecinctPlan === null
              ? `4px solid ${colours.th2}`
              : "4px solid transparent",
        }}
      >
        <StyledRadioContainer>
          <StyledRadioInput
            type="radio"
            id="NoNativeVegPrecinctPlan"
            name="nativeVegPrecinctPlan"
            value={"false"}
            onChange={onNativeVegPrecinctPlanRadioChange}
            checked={state.nativeVegPrecinctPlan === false}
          />
          <StyledRadioLabel htmlFor={"NoNativeVegPrecinctPlan"}>
            No
          </StyledRadioLabel>
        </StyledRadioContainer>
        <StyledRadioContainer>
          <StyledRadioInput
            type="radio"
            id="YesNativeVegPrecinctPlan"
            name="nativeVegPrecinctPlan"
            value={"true"}
            onChange={onNativeVegPrecinctPlanRadioChange}
            checked={state.nativeVegPrecinctPlan === true}
          />
          <StyledRadioLabel htmlFor={"YesNativeVegPrecinctPlan"}>
            Yes
          </StyledRadioLabel>
        </StyledRadioContainer>
      </StyledRadioGroupContainer>
      <div
        style={{
          display: state.nativeVegPrecinctPlan === true ? "block" : "none",
        }}
      >
        <p>
          Provide the name of the NVPP and demonstrate that the removal of this
          native vegetation will not:
        </p>
        <ul>
          <li>Undermine or contradict any stated objective of the NVPP or;</li>
          <li>
            Negatively impact or result in the fragmentation of other native
            vegetation identified for retention within the NVPP.
          </li>
        </ul>
        <StyledTextArea
          maxLength={3000}
          placeholder={nativeVegPrecinctPlanPlaceholder}
          value={state.nativeVegPrecinctPlanText}
          rows={8}
          onChange={(e) =>
            dispatch({
              type: "change",
              payload: {
                prop: "nativeVegPrecinctPlanText",
                value: e.target.value,
              },
            })
          }
          style={{ height: "230px" }}
        ></StyledTextArea>
      </div>

      <SectionTitle>Application Requirement 9 - Offset statement</SectionTitle>
      <p>
        The responsible authority uses this information to gain confidence that
        an offset requirement, as identified in the Native Vegetation Removal
        Report, has been located and will be secured before the native
        vegetation is removed.
      </p>
      <p>
        If you are proposing to secure a third-party offset, you must
        demonstrate that:
      </p>
      <ul>
        <li>You have located a compliant offset;</li>
        <li>You are prepared to purchase the offset; and</li>
        <li>The offset owner is prepared to sell you the offset.</li>
      </ul>
      <p>
        The availability of third-party offsets can be checked using the{" "}
        <Link
          href="https://nvcr.delwp.vic.gov.au/"
          target="_blank"
          title="Native Vegetation Credit Register Search Tool"
          rel="noopener noreferrer"
        >
          Native Vegetation Credit Register Search Tool
        </Link>
        <ExternalLinkIcon />.{" "}
        <Link
          href={medianCreditPricing}
          target="_blank"
          title="Median credit pricing"
          rel="noopener noreferrer"
        >
          This sheet
        </Link>
        <ExternalLinkIcon /> presents a comparative analysis of General Habitat
        Unit (GHU) pricing across various Catchment Management Authority
        jurisdictions. While third-party offset trade prices remain
        market-oriented and fluctuate, the provided data highlights the median
        pricing for GHUs in each region.
      </p>
      <p>If you are proposing to secure a first-party offset, you must:</p>
      <ul>
        <li>
          Demonstrate that the proposed area can generate sufficient gain via a
          Native Vegetation Offset Report;
        </li>
        <li>
          Acknowledge your landowner obligations and the required land
          management activities; and
        </li>
        <li>
          Confirm that a statutory authority has agreed to secure the area as an
          offset site.
        </li>
      </ul>
      <StyledTextArea
        maxLength={3000}
        placeholder={offsetStatementPlaceholder}
        value={state.offsetStatement}
        rows={9}
        onChange={(e) =>
          dispatch({
            type: "change",
            payload: { prop: "offsetStatement", value: e.target.value },
          })
        }
        style={{ height: "250px" }}
      ></StyledTextArea>
    </Container>
  );
}

export default AppRequirements;
